import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Info, Save, ShieldCheck, Mail, KeyRound, Pencil, Trash2, TrendingUp, CheckCircle2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import PhotoPicker from "@/components/PhotoPicker";
import AvatarPicker from "@/components/AvatarPicker";
import SuggestCategory from "@/components/SuggestCategory";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import PriceTypePicker from "@/components/PriceTypePicker";
import { CATEGORIES, findCategory } from "@/lib/categories";
import { rememberMyAd } from "@/lib/myAds";
import { trackSubmitListing } from "@/lib/analytics";
import { listingSpamReason } from "@/lib/moderation";

const EMPTY = {
  title: "",
  description: "",
  category: CATEGORIES[0].slug,
  subcategory: "",
  price: "",
  price_type: "fixed",
  location: "",
  serial_number: "",
  ean_code: "",
  vin_code: "",
  year_of_manufacture: "",
  machine_hours: "",
  weight_kg: "",
  photos: [],
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  owner_password: "",
  owner_avatar: null,
};

export default function CreateAdPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null); // { listing, email_status, manage_token }
  const [showPw, setShowPw] = useState(false);

  const currentCat = useMemo(() => findCategory(form.category), [form.category]);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const blocked = listingSpamReason(form);
      if (blocked) {
        setError(blocked);
        setBusy(false);
        return;
      }
      const isMachine = form.category === "stroje-tezka-technika";
      const showYear = isMachine || form.subcategory === "elektricke-aku-naradi";
      const toNum = (v) => (v === "" || v == null ? null : Number(v));
      const payload = {
        title: form.title,
        description: form.description,
        category: form.category,
        subcategory: form.subcategory || null,
        price: form.price_type === "fixed" && form.price !== "" ? Number(form.price) : null,
        price_type: form.price_type,
        location: form.location || null,
        serial_number: form.serial_number || null,
        ean_code: form.ean_code || null,
        vin_code: isMachine ? form.vin_code || null : null,
        year_of_manufacture: showYear ? toNum(form.year_of_manufacture) : null,
        machine_hours: isMachine ? toNum(form.machine_hours) : null,
        weight_kg: isMachine ? toNum(form.weight_kg) : null,
        photos: form.photos,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone || null,
        owner_password: form.owner_password,
        owner_avatar: form.owner_avatar,
      };
      const { data } = await api.post("/listings", payload);
      // Remember this ad as mine on this device.
      if (data?.listing?.id) {
        rememberMyAd(data.listing.id, {
          manage_token: data.listing.manage_token,
          contact_email: data.listing.owner_email || form.contact_email,
        });
      }
      trackSubmitListing({
        listingId: data?.listing?.id,
        category: data?.listing?.category || form.category,
      });
      setSuccess(data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return <SuccessScreen data={success} password={form.owner_password} email={form.contact_email} onAnother={() => { setSuccess(null); setForm(EMPTY); }} />;
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <h1 className="stb-h text-4xl text-[#0E0E0E]">Nový inzerát</h1>
        <p className="text-sm text-[#0E0E0E] mt-1">
          Žádná registrace. Vyplň inzerát + svůj e-mail, telefon a heslo k inzerátu.
          Odkaz na úpravu/smazání ti pošleme e-mailem.
        </p>

        <form onSubmit={submit} className="stb-card p-5 mt-4 space-y-4" data-testid="create-ad-form">
          <Field label="Název inzerátu *">
            <input
              className="stb-input"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              required minLength={3} maxLength={120}
              placeholder="Např. Bosch GBH 2-26 vrtací kladivo SDS+"
              data-testid="ad-title-input"
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Kategorie *">
              <select className="stb-input" value={form.category}
                onChange={(e) => { update("category", e.target.value); update("subcategory", ""); }}
                data-testid="ad-category-select">
                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Podkategorie">
              <select className="stb-input" value={form.subcategory}
                onChange={(e) => update("subcategory", e.target.value)}
                data-testid="ad-subcategory-select">
                <option value="">— vyber —</option>
                {currentCat?.subcategories.map((sc) => (
                  <option key={sc.slug} value={sc.slug}>{sc.name}</option>
                ))}
              </select>
            </Field>
          </div>

          <SuggestCategory compact />

          <Field label="Popis *">
            <textarea className="stb-input" value={form.description}
              onChange={(e) => update("description", e.target.value)}
              required minLength={10} maxLength={8000}
              placeholder="Stav, stáří, kompletnost, lokalita…"
              data-testid="ad-description-input" />
          </Field>

          <div>
            <h3 className="stb-label mb-2">Cena *</h3>
            <PriceTypePicker
              value={form.price_type}
              onChange={(v) => { update("price_type", v); if (v !== "fixed") update("price", ""); }}
              testIdPrefix="ad-price-type"
            />
            {form.price_type === "fixed" && (
              <div className="mt-3 grid md:grid-cols-2 gap-3">
                <Field label="Částka v Kč *">
                  <input className="stb-input" type="number" min={0}
                    value={form.price} onChange={(e) => update("price", e.target.value)}
                    placeholder="např. 12500" data-testid="ad-price-input"
                    required />
                </Field>
              </div>
            )}
          </div>

          <Field label="Lokalita *">
            <LocationAutocomplete
              value={form.location}
              onChange={(v) => update("location", v)}
              required
              testId="ad-location-input"
            />
            <p className="stb-mono text-xs text-[#A3A3A3] mt-1">
              Začni psát – našeptávač navrhne město nebo obec. PSČ stačí napsat.
            </p>
          </Field>

          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Výrobní číslo"><input className="stb-input" value={form.serial_number}
              onChange={(e) => update("serial_number", e.target.value)} data-testid="ad-serial-input" /></Field>
            <Field label="EAN kód"><input className="stb-input" value={form.ean_code}
              onChange={(e) => update("ean_code", e.target.value)} data-testid="ad-ean-input" /></Field>
          </div>

          {form.category === "stroje-tezka-technika" && (
            <>
              <Field label="VIN kód (jen u strojů a techniky)">
                <input className="stb-input" value={form.vin_code}
                  onChange={(e) => update("vin_code", e.target.value)}
                  placeholder="17místný kód" maxLength={40} data-testid="ad-vin-input" />
              </Field>
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="Rok výroby">
                  <input className="stb-input" type="number" min={1900} max={2100}
                    value={form.year_of_manufacture}
                    onChange={(e) => update("year_of_manufacture", e.target.value)}
                    placeholder="např. 2018" data-testid="ad-year-input" />
                </Field>
                <Field label="Motohodiny (Mth)">
                  <input className="stb-input" type="number" min={0}
                    value={form.machine_hours}
                    onChange={(e) => update("machine_hours", e.target.value)}
                    placeholder="např. 3 200" data-testid="ad-hours-input" />
                </Field>
                <Field label="Hmotnost (kg)">
                  <input className="stb-input" type="number" min={0}
                    value={form.weight_kg}
                    onChange={(e) => update("weight_kg", e.target.value)}
                    placeholder="např. 8 500" data-testid="ad-weight-input" />
                </Field>
              </div>
            </>
          )}

          {form.category !== "stroje-tezka-technika" && form.subcategory === "elektricke-aku-naradi" && (
            <Field label="Rok výroby">
              <input className="stb-input" type="number" min={1900} max={2100}
                value={form.year_of_manufacture}
                onChange={(e) => update("year_of_manufacture", e.target.value)}
                placeholder="např. 2022" data-testid="ad-year-input" />
            </Field>
          )}

          <Field label="Fotografie (max 20)">
            <PhotoPicker photos={form.photos} onChange={(p) => update("photos", p)} testId="ad-photos" />
          </Field>

          <hr className="stb-divider" />
          <h3 className="stb-label">Tvoje kontaktní údaje</h3>

          <Field label="Profilová fotka (volitelné)">
            <AvatarPicker value={form.owner_avatar} onChange={(v) => update("owner_avatar", v)} />
            <p className="text-xs text-[#A3A3A3] mt-2 flex items-start gap-1">
              <Info size={12} className="mt-0.5 shrink-0" />
              Nahrání profilové fotky <strong className="text-[#FF6B00]">prokazatelně zvyšuje prodej</strong> – kupující vidí reálnou osobu.
            </p>
          </Field>

          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Jméno / přezdívka *">
              <input className="stb-input" value={form.contact_name}
                onChange={(e) => update("contact_name", e.target.value)}
                required data-testid="ad-contact-name" />
            </Field>
            <Field label="Telefon (volitelný, skrytý)">
              <input className="stb-input" value={form.contact_phone}
                onChange={(e) => update("contact_phone", e.target.value)}
                placeholder="V inzerátu se nezobrazí" data-testid="ad-contact-phone" />
            </Field>
          </div>

          <Field label="E-mail (skrytý, pro správu inzerátu) *">
            <input className="stb-input" type="email" value={form.contact_email}
              onChange={(e) => update("contact_email", e.target.value)}
              required placeholder="tvuj@email.cz" data-testid="ad-contact-email" />
          </Field>

          <Field label="Heslo k inzerátu *">
            <div className="relative">
              <input className="stb-input pr-12" type={showPw ? "text" : "password"}
                value={form.owner_password}
                onChange={(e) => update("owner_password", e.target.value)}
                minLength={4} required
                placeholder="Min. 4 znaky. Slouží pro úpravy a smazání inzerátu."
                data-testid="ad-owner-password" />
              <button type="button" onClick={() => setShowPw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#FF6B00]"
                tabIndex={-1} aria-label="Zobrazit heslo">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-[#A3A3A3] mt-1 flex items-start gap-1">
              <ShieldCheck size={12} className="mt-0.5 shrink-0 text-[#FF6B00]" />
              Heslo si pamatuj. Slouží pro úpravu/smazání inzerátu i jako pojistka, kdybys ztratil/a potvrzovací e-mail.
            </p>
          </Field>

          {error && (
            <p className="text-[#FF6B00] stb-mono text-sm" data-testid="create-ad-error">{error}</p>
          )}

          <div className="flex gap-2">
            <button type="submit" className="stb-btn stb-btn-primary" disabled={busy}
              data-testid="create-ad-submit">
              <Save size={14} /> {busy ? "Ukládám…" : "Vložit inzerát zdarma"}
            </button>
            <button type="button" onClick={() => navigate("/")} className="stb-btn stb-btn-ghost">
              Zrušit
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  // Use <div>, not <label>: file inputs + picker buttons inside a label break
  // gallery uploads on mobile (gesture swallowed / empty change event).
  return (
    <div className="block">
      <span className="stb-label">{label}</span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SuccessScreen({ data, password, email, onAnother }) {
  const navigate = useNavigate();
  const listing = data.listing || {};
  const emailStatus = data.email_status || {};
  const mockMode = emailStatus.mock === true;
  const managePath = `/sprava-inzeratu/${listing.id}?token=${listing.manage_token}`;
  const manageUrl = `${window.location.origin}${managePath}`;
  return (
    <Layout>
      <div className="max-w-2xl mx-auto stb-card p-6" data-testid="create-ad-success">
        <div className="stb-stripes h-2 w-24 mb-4" />
        <h1 className="stb-h text-3xl text-white flex items-center gap-2">
          <CheckCircle2 className="text-[#22C55E]" size={30} /> Inzerát byl úspěšně vložen 🎯
        </h1>
        <p className="text-[#A3A3A3] mt-2">
          Tvůj inzerát „<span className="text-white">{listing.title}</span>" je teď online.
        </p>

        {/* E-mail + heslo */}
        <div className="stb-card p-4 mt-4 border-[#262626] bg-[#1a1a1a] space-y-3">
          <div className="flex items-start gap-2">
            <Mail size={16} className="text-[#FF6B00] mt-1 shrink-0" />
            <div>
              <p className="stb-label">Potvrzení odesláno na e-mail</p>
              <p className="text-white text-sm break-all" data-testid="success-email">{email || listing.contact_email}</p>
              {mockMode && (
                <p className="text-[#FFB800] text-xs mt-1">
                  (E-mail se teď reálně neodesílá – ulož si prosím odkaz a heslo níže.)
                </p>
              )}
            </div>
          </div>
          <div className="flex items-start gap-2">
            <KeyRound size={16} className="text-[#FF6B00] mt-1 shrink-0" />
            <div>
              <p className="stb-label">Tvoje heslo ke správě inzerátu</p>
              <p className="stb-mono text-white text-base" data-testid="success-password">{password || "—"}</p>
              <p className="text-[#A3A3A3] text-xs mt-1">Ulož si ho – slouží ke správě inzerátu jako záloha k odkazu.</p>
            </div>
          </div>
        </div>

        {/* Správní odkaz */}
        <div className="stb-card p-4 mt-4 border-[#FFB800] bg-[#1a1a1a]">
          <p className="stb-label text-[#FFB800]">Tvůj správní odkaz</p>
          <p className="stb-mono text-xs text-white break-all mt-1" data-testid="manage-url">{manageUrl}</p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <button className="stb-btn stb-btn-secondary" onClick={() => navigator.clipboard?.writeText(manageUrl)} data-testid="copy-manage-url">
              Zkopírovat odkaz
            </button>
            <button className="stb-btn stb-btn-primary" onClick={() => navigate(`/inzerat/${listing.id}`)} data-testid="goto-ad">
              Otevřít inzerát
            </button>
          </div>
        </div>

        {/* Úprava / smazání / topování */}
        <div className="mt-5">
          <p className="text-white font-semibold mb-2">Úprava, smazání nebo topování je možné zde:</p>
          <div className="flex gap-2 flex-wrap">
            <button className="stb-btn stb-btn-secondary" onClick={() => navigate(managePath)} data-testid="success-edit">
              <Pencil size={16} /> Upravit
            </button>
            <button className="stb-btn stb-btn-primary" onClick={() => navigate(managePath)} data-testid="success-top">
              <TrendingUp size={16} /> TOPovat
            </button>
            <button className="stb-btn stb-btn-secondary" onClick={() => navigate(managePath)} data-testid="success-delete">
              <Trash2 size={16} /> Smazat
            </button>
          </div>
        </div>

        <div className="mt-6 flex gap-2 flex-wrap border-t border-[#262626] pt-4">
          <button className="stb-btn stb-btn-secondary" onClick={onAnother} data-testid="another-ad">
            Vložit další inzerát
          </button>
          <button className="stb-btn stb-btn-ghost" onClick={() => navigate("/")}>Na hlavní stránku</button>
        </div>
      </div>
    </Layout>
  );
}
